import {Signal} from '../signals-screen/types';
import {DeviceFilterType} from '../../services/types';
import {DateRangeValue} from '../../utils/time';
import {ATTENTION_METRIC_NAME} from '../../shared/types/common';
export type HistoryMessage = {
  role: string;
  content: string;
}

export type FocusArea = {
  title: string;
  subQuestions: string[];
}

export type WebInsight = {
  insight: string;
  sources: {
    title: string,
    name: string,
    link: string
  }[];
}

export type WebInsights = WebInsight[];

export type FocusAreasMap = Record<string, { subQuestions: string[] }>;

export type WizardPayload = {
  type: string;
  focusAreas: Record<string, FocusArea>;
  brands: string[];
  competitors: string[];
  geos: string[];
  startDate: string;
  endDate: string;
};

export type AlertPayload = {
  alert_type: string;
  metric: string;
  change: {
    direction: string;
    percent: string;
  };
  date_before: string;
  date_after: string;
  brand_key: string;
}

export type WidgetContext = {
  name?: DashboardWidget;
  metrics: string[];
  myBrand: string;
  myCompetitors: string[];
  dateRange: DateRangeValue;
  countries: string[];
  device: DeviceFilterType;
}

export type CopilotMessagePayload = {
  requestId: string;
  userPrompt: string;
  sessionContext: {
    sessionId: string;
    customerName: string;
    customerBrandsList: string[];
  };
  workflowContext?: string;
  conversationHistory: HistoryMessage[];
  agencyId: string;
  debug?: boolean;
  wizard?: WizardPayload;
  alert?: AlertPayload;
  signal?: Signal;
  widgetContext?: WidgetContext;
}

export type CopilotMessageResponse = {
  requestId: string;
  sessionId: string;
  userId: string;
  status: string;
  timestamp: string;
  answer: {
    text: string;
    insights: string;
  };
  graph: graphSpec[];
  followUpQuestions: string[];
  creativesURLs: string[];
  originalResponse?:any;
  lambdaId?: string;
}

export type Wizard = {
    type:  string;
    focusAreas: FocusAreasMap;
    title: string;
    description: string;
    tileSvg: string;
    wizardSvg: string;
}

export type graphSpec = {
  title: string;
  type: 'bar'| 'line' | 'doughnut';
  url?: string;
  x: string[];
  series: graphSeries[];
  y_is_percentage?: boolean;
  x_axis: string;
  y_axis: string;
}

export type graphSeries = {
  name: string;
  y: number[];
  unit?: string;
}

export enum DashboardWidget {
  SPEND_ANALYSIS = 'Spend Analysis',
  CREATIVE_GALLERY = 'Creative Gallery',
  CREATIVE_GALLERY_COMPETITION = 'Creative Gallery Competition',
  CREATIVE_GALLERY_MESSAGING = 'Creative Gallery Messaging',
  PERFORMANCE_MAP = 'Performance Map',
  PERFORMANCE_OVER_TIME = 'Performance Over Time',
  MY_COMPETITION_OVERVIEW = 'Overview',
  AUDIENCE_INTERESTS = 'Audience Interests',
  PERFORMANCE_BY_DEVICE = 'Performance by Device',
  SHARE_OF_VOICE = 'Share of Voice',
}

const CREATIVE_GALLERY_COMPETITORS_QUESTIONS = [
  "Which phrases are most frequently used in my competitors' ads?",
  "What factors are contributing to my competitor's top creative's success?",
  "What can I do to improve my creative effectiveness based on the competition?"
];

export const WIDGET_QUESTIONS_MAP = {
  [DashboardWidget.SPEND_ANALYSIS]: [
    "How does my spend efficiency compare against industry benchmarks?",
    "How can I scale my campaign CTR success, based on my competitions strategies?",
    "Where am I seeing the biggest gaps in efficiency vs. my competitors?",
    "What are the biggest drivers of inefficient spend in my current dataset?"
  ],
  [DashboardWidget.CREATIVE_GALLERY]: [
    `What are my most effective creatives based on CTR, ${ATTENTION_METRIC_NAME}, and impressions?`,
    "What can I do to improve my creative effectiveness based on the competition?",
    "What factors are contributing to the success of my top creatives?"
  ],
  [DashboardWidget.CREATIVE_GALLERY_COMPETITION]: CREATIVE_GALLERY_COMPETITORS_QUESTIONS,
  [DashboardWidget.CREATIVE_GALLERY_MESSAGING]: CREATIVE_GALLERY_COMPETITORS_QUESTIONS,
  [DashboardWidget.PERFORMANCE_MAP]: [
    `Which states should my brand prioritize for expansion based on cost efficiency and performance (${ATTENTION_METRIC_NAME}, dwell time, CTR)?`,
    `Which states are my competitors seeing the most success in (highest CTR, highest ${ATTENTION_METRIC_NAME}, lowest CPM)?`,
    "Based on competitor success, which states should I target?",
    "Which of the countries I’m targeting are underperforming or overperforming compared to my competitors, and why?"
  ],
  [DashboardWidget.PERFORMANCE_OVER_TIME]: [
    "Are gaps versus competitors widening or narrowing over time?",
    "What explains the performance trends across the selected brands?",
  ],
  [DashboardWidget.MY_COMPETITION_OVERVIEW]: [
    `My brand’s CPM is higher than my competitors’, but my ${ATTENTION_METRIC_NAME} is lower. Why? How can I improve this?`,
    "My brand is achieving a higher CTR than my competitors with a lower CPM. Why? How can I scale this approach?",
    `Which audience segments generate the highest ${ATTENTION_METRIC_NAME} for my brand compared to my competitors?`
  ],
  [DashboardWidget.AUDIENCE_INTERESTS]: [
    "Which competitors are most heavily investing in the subcategories within my primary audience interest category?",
    "Are the interests I'm leading in actually more engaging than the ones my competitors own?",
    "Which interests am I over-investing in relative to the engagement they deliver?",
    "Which interest categories show high engagement potential but low competitive presence, including mine?"
  ],
  [DashboardWidget.PERFORMANCE_BY_DEVICE]: [
    `Which competitor is achieving the highest ${ATTENTION_METRIC_NAME} score on mobile, and what ad sizes are they using?`,
    "Is any competitor gaining ground on a specific device over the last 90 days?",
    `What optimizations should I make to improve my ${ATTENTION_METRIC_NAME} score on the device where I am weakest?`
  ],
  [DashboardWidget.SHARE_OF_VOICE]: [
    "Which competitor has grown their share of voice the most over the past 90 days, and what's driving it?",
    "What strategies are supporting my competitors' share of voice presence?"
  ]
}

export const ASK_POLARIS_SIDE_DRAWER_WIDTH = '700px';

export const POLARIS_HOME_SUGGESTED_QUESTIONS_AMOUNT: number = 3;
export const POLARIS_HOME_SUGGESTED_QUESTIONS = [
  "How can I improve my CTR based on what works for my competitors?",
  "How can I scale CTR gains without increasing CPM?",
  "What is currently limiting my CTR the most?",
  "Where am I overpaying relative to performance (CPM vs CTR)?",
  "Which of my creatives perform best by CTR, attention, and impressions?",
  "Which of my competitors' creatives perform best, and why?",
  "Which of my creatives underperform by CTR or CPM (with sufficient impressions)?",
  "How does a specific creative perform compared to similar creatives in size and volume?",
  "What creative optimizations would most likely improve my results?",
  "What factors contribute to the success of my top creative?",
  "What are my competitors doing better than I right now?",
  "Where do I outperform my competitors, and why?",
  "Which competitor strategies are driving the biggest performance gaps?",
  "Based on competitor success, which geographies should I prioritize next?",
  "Which audiences drive high attention but low engagement, and why?",
  "What audience segments should I scale based on current performance?",
  "Which targeting strategies are driving the strongest results for my competitors?",
  "Where is my targeting too broad or too narrow compared to competitors?",
  "How can I refine my targeting to improve efficiency without losing scale?",
  "How should I reallocate spend across audiences and targeting to improve results?"
];
